import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { CustomTaskDialog, GeneralTasksService } from '@roc/feature-general-tasks';
import { Button, ConfirmDialog } from '@roc/ui';
import { UnclassifiedDocumentsEmptyState } from './unclassifiedDocumentsEmptyState';
import { UnclassifiedDocumentsBanner } from './unclassifiedDocumentsBanner';
import { UnclassifiedDocumentsTable } from './unclassifiedDocumentsTable';
import {
  LoanTask,
  LoanTaskSection,
  NewTaskFormValues,
  UnclassifiedDocument,
  UnclassifiedDocumentListResponse,
} from './unclassifiedDocumentsTypes';
import { UnclassifiedDocumentPreview, UnclassifiedDocumentPreviewData } from './unclassifiedDocumentPreview';
import { UnclassifiedDocumentsService } from '../../../service/unclassifiedDocumentsService';
import { NewTaskForDocumentDialog } from './newTaskForDocumentDialog';

const UNCLASSIFIED_DOCUMENTS_TITLE = 'Unclassified Documents';
/** This screen's own task — never a destination for the documents it lists. */
const UNCLASSIFIED_DOCUMENTS_TASK_CODE = 'Unclassified Documents';
/** Task code of every task created from this screen's "Create new task…" option. */
const NEW_TASK_TASK_CODE = 'Document Upload';
/** Tasks created from this screen are always for the external team (the form's former default). */
const NEW_TASK_INTERNAL = false;

const unclassifiedDocumentsService = new UnclassifiedDocumentsService();
// Created on first use, not at import time: feature-general-tasks -> feature-documents -> this
// lib -> feature-general-tasks is an import cycle, so GeneralTasksService is still undefined
// while this module is first evaluated.
let generalTasksService: GeneralTasksService | null = null;
const getGeneralTasksService = () => {
  if (!generalTasksService) {
    generalTasksService = new GeneralTasksService();
  }
  return generalTasksService;
};

/**
 * getLoanTasks returns { sectionName: tasks[] }, already ordered by section — sections are left in
 * that order (same order as the rest of the portal), only the tasks within each one are sorted.
 * Parent tasks only group their children (they are not real destinations) and are dropped, as is
 * any task whose code isn't in the whitelist (allowedTaskCodes, UI change 22-oct-2026 — Sourav: the
 * dropdown now only offers whitelisted tasks, ordered alphabetically by title), and sections left
 * with no tasks after that.
 */
const toSections = (tasksBySection: Record<string, LoanTask[]>, allowedTaskCodes: string[]): LoanTaskSection[] => {
  const allowed = new Set((allowedTaskCodes ?? []).map(code => code.toLowerCase()));
  return Object.entries(tasksBySection ?? {})
    .map(([name, tasks]) => ({
      name,
      tasks: (tasks ?? [])
        .filter(task =>
          !task.parentTask &&
          task.taskCode !== UNCLASSIFIED_DOCUMENTS_TASK_CODE &&
          !!task.taskCode &&
          allowed.has(task.taskCode.toLowerCase())
        )
        .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' })),
    }))
    .filter(section => section.tasks.length > 0);
};

/** Prefill both dropdowns from the AI's suggested taskId, when that task is one of the options. */
const getInitialSelection = (documents: UnclassifiedDocument[], sections: LoanTaskSection[]) => {
  const selectedSections: Record<number, string> = {};
  const selectedTasks: Record<number, number> = {};
  documents.forEach(document => {
    if (!document.taskId) return;
    const section = sections.find(s => s.tasks.some(task => task.taskId === document.taskId));
    if (section) {
      selectedSections[document.id] = section.name;
      selectedTasks[document.id] = document.taskId;
    }
  });
  return { selectedSections, selectedTasks };
};

/** Backend ValidationExceptions (400) carry a user-facing message. */
const getErrorMessage = (error: any, fallback: string) =>
  error?.error?.response?.data?.error?.message || fallback;

export interface UnclassifiedDocumentsDialogProps {
  open: boolean;
  handleClose: () => void;
  taskId: number;
  loanId: number;
}

export const UnclassifiedDocumentsDialog = observer(({
  open,
  handleClose,
  taskId,
  loanId,
}: UnclassifiedDocumentsDialogProps) => {
  const { globalStore } = useBaseStore();
  const { userStore } = useUserStore();
  const [documents, setDocuments] = useState<UnclassifiedDocument[]>([]);
  const [sections, setSections] = useState<LoanTaskSection[]>([]);
  const [selectedSections, setSelectedSections] = useState<Record<number, string>>({});
  const [selectedTasks, setSelectedTasks] = useState<Record<number, number>>({});
  /** documentId -> true when "Addendum for Purchase Contract" is selected (selectedTasks then holds the Purchase Contract task's own taskId). */
  const [selectedAddendums, setSelectedAddendums] = useState<Record<number, boolean>>({});
  const [anyProcessing, setAnyProcessing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [preview, setPreview] = useState<UnclassifiedDocumentPreviewData | null>(null);
  /** Document whose "Create new task…" dialog is open. */
  const [newTaskDocument, setNewTaskDocument] = useState<UnclassifiedDocument | null>(null);
  /** Document waiting for the Discard confirmation. */
  const [discardDocument, setDiscardDocument] = useState<UnclassifiedDocument | null>(null);

  const showError = (message: string) => globalStore.notificationStore.showErrorNotification({ message });

  const fetchData = async () => {
    try {
      const [documentsResponse, tasksResponse]: any[] = await Promise.all([
        unclassifiedDocumentsService.getDocuments(loanId),
        unclassifiedDocumentsService.getUnderwritingTasks(loanId),
      ]);
      const list: UnclassifiedDocumentListResponse = documentsResponse.data?.data;
      const nextDocuments = list?.documents ?? [];
      const nextSections = toSections(tasksResponse.data?.data, list?.allowedTaskCodes ?? []);
      const initialSelection = getInitialSelection(nextDocuments, nextSections);
      setDocuments(nextDocuments);
      setAnyProcessing(!!list?.anyProcessing);
      setSections(nextSections);
      setSelectedSections(initialSelection.selectedSections);
      setSelectedTasks(initialSelection.selectedTasks);
      setSelectedAddendums({});
    } catch (error) {
      showError(getErrorMessage(error, 'Error while loading the unclassified documents.'));
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!open || !loanId) return;
    setLoaded(false);
    fetchData();
  }, [open, loanId]);

  // Every listed document is PENDING (the backend filters out SAVED ones).
  const pendingCount = documents.length;

  const handleSectionChange = (documentId: number, sectionName: string) => {
    setSelectedSections(prev => ({ ...prev, [documentId]: sectionName }));
    // The previous task belongs to the previous section.
    setSelectedTasks(prev => {
      const next = { ...prev };
      delete next[documentId];
      return next;
    });
    setSelectedAddendums(prev => ({ ...prev, [documentId]: false }));
  };

  const handleTaskChange = (documentId: number, taskId: number, isAddendum = false) => {
    setSelectedTasks(prev => ({ ...prev, [documentId]: taskId }));
    setSelectedAddendums(prev => ({ ...prev, [documentId]: isAddendum }));
  };

  const handleCreateTask = async (document: UnclassifiedDocument, values: NewTaskFormValues) => {
    // Every task of a section shares its category and property/borrower, so the new one copies them.
    const sectionTask = sections.find(s => s.name === selectedSections[document.id])?.tasks[0];
    if (!sectionTask) return;
    const userId = userStore.userInformation?.userId;

    setSavingId(document.id);
    let newTaskId: number | undefined;
    try {
      // Same request as the Create Task dialog (AddLoanTaskDialog), assigned to whoever classifies.
      const response: any = await getGeneralTasksService().addLoanTask({
        taskName: values.title,
        taskCode: NEW_TASK_TASK_CODE,
        loanId,
        sectionName: sectionTask.category,
        type2: sectionTask.type2,
        type_id2: sectionTask.typeId2,
        internalTask: NEW_TASK_INTERNAL,
        assigneeId: userId,
        ownerId: userId,
        observers: [],
      }, globalStore.isLoanChatApp);
      newTaskId = response?.data?.data?.taskId;
      if (!newTaskId) throw new Error('No taskId returned');
    } catch (error) {
      showError(getErrorMessage(error, 'Error while creating the task.'));
      setSavingId(null);
      return;
    }

    setNewTaskDocument(null);
    try {
      await unclassifiedDocumentsService.save(document.id, { taskId: newTaskId });
      globalStore.notificationStore.showSuccessNotification({
        message: `Task "${values.title}" created and ${document.fileName} saved to it.`,
      });
    } catch (error) {
      // The task exists now; after the refresh it is in the Task dropdown, so a retry is just Save.
      showError(getErrorMessage(error, `Task "${values.title}" was created, but ${document.fileName} could not be saved to it.`));
    } finally {
      await fetchData();
      setSavingId(null);
    }
  };

  const handlePreview = async (document: UnclassifiedDocument) => {
    try {
      const response: any = await unclassifiedDocumentsService.getFile(document.id);
      const headers = response.headers ?? {};
      setPreview({
        fileName: document.fileName,
        contentType: headers['content-type'] ?? 'application/octet-stream',
        data: response.data,
        headers,
      });
    } catch (error) {
      showError(getErrorMessage(error, 'Error while loading the document preview.'));
    }
  };

  const handleDiscard = async () => {
    const document = discardDocument;
    if (!document) return;

    setDiscardDocument(null);
    setSavingId(document.id);
    try {
      await unclassifiedDocumentsService.discard(document.id);
      globalStore.notificationStore.showSuccessNotification({ message: `${document.fileName} discarded.` });
      await fetchData();
    } catch (error) {
      showError(getErrorMessage(error, `Error while discarding ${document.fileName}.`));
    } finally {
      setSavingId(null);
    }
  };

  const handleSave = async (document: UnclassifiedDocument) => {
    const taskId = selectedTasks[document.id];
    if (!taskId) return;
    const isAddendum = !!selectedAddendums[document.id];

    setSavingId(document.id);
    try {
      await unclassifiedDocumentsService.save(document.id, { taskId, isAddendum });
      globalStore.notificationStore.showSuccessNotification({ message: `${document.fileName} saved.` });
      await fetchData();
    } catch (error) {
      showError(getErrorMessage(error, `Error while saving ${document.fileName}.`));
    } finally {
      setSavingId(null);
    }
  };

  const dialogContent = !loaded ? null : (
    <Grid container spacing={3}>
      {documents.length === 0 ? (
        <Grid item xs={12}>
          <UnclassifiedDocumentsEmptyState />
        </Grid>
      ) : (
        <>
          {(anyProcessing || pendingCount > 0) && (
            <Grid item xs={12}>
              <UnclassifiedDocumentsBanner
                pendingCount={pendingCount}
                anyProcessing={anyProcessing}
                onRefresh={fetchData}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <UnclassifiedDocumentsTable
              documents={documents}
              sections={sections}
              selectedSections={selectedSections}
              selectedTasks={selectedTasks}
              selectedAddendums={selectedAddendums}
              onSectionChange={handleSectionChange}
              onTaskChange={handleTaskChange}
              onCreateTask={setNewTaskDocument}
              onPreview={handlePreview}
              onSave={handleSave}
              onDiscard={setDiscardDocument}
              locked={anyProcessing || savingId != null}
            />
          </Grid>
        </>
      )}
    </Grid>
  );

  const dialogActions = (
    <Grid container justifyContent="flex-end">
      <Grid item>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          testId="close"
        >
          Close
        </Button>
      </Grid>
    </Grid>
  );

  // DocumentForms mounts every dialog up front with no taskId; CustomTaskDialog must only mount
  // once there is one (same guard as PurchaseContractReviewDialog), or its effect crashes the page.
  if (!taskId) {
    return null;
  }

  return (
    <>
      <CustomTaskDialog
        open={open}
        maxWidth="lg"
        title={UNCLASSIFIED_DOCUMENTS_TITLE}
        taskId={taskId}
        handleClose={handleClose}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
      />
      <UnclassifiedDocumentPreview preview={preview} onClose={() => setPreview(null)} />
      <NewTaskForDocumentDialog
        document={newTaskDocument}
        sectionName={newTaskDocument ? selectedSections[newTaskDocument.id] : ''}
        saving={savingId != null}
        onClose={() => setNewTaskDocument(null)}
        onCreate={values => handleCreateTask(newTaskDocument, values)}
      />
      <ConfirmDialog
        open={!!discardDocument}
        title="Discard document"
        body={`Discard ${discardDocument?.fileName ?? 'this document'}? It will be removed from Unclassified Documents without being saved to any task.`}
        confirmButtonText="Discard"
        cancelButtonText="Cancel"
        handleConfirm={handleDiscard}
        handleClose={() => setDiscardDocument(null)}
        handleCancel={() => setDiscardDocument(null)}
      />
    </>
  );
});
